import qs from 'qs';

import {
  BulkCreateSfOpportunitiesBody,
  BulkCreateSfOpportunitiesResponse,
  ControlCentreProperty,
  CreateSfOpportunityBody,
  CreateSfOpportunityResponse,
  PaginatedPriceParrotQueryParams,
  PaginatedPriceParrotResponse,
  PriceSummaryJob,
  PriceSummaryJobBody,
  PriceSummaryJobQueryParams,
  Property,
  RawControlCentreProperty,
  RawControlCentreResponse,
  ScheduledPriceSummaryJob,
  ScheduledPriceSummaryJobBody,
  ScheduledPriceSummaryJobRun,
  ScheduledPriceSummaryJobVariation,
  ScheduledPriceSummaryJobVariationBody,
  TriggerNotificationBody,
  TriggerNotificationResponse,
} from '~/types/services/priceParrot';

import { json_headers, request } from './common';

function basePath() {
  return window.configs.API_HOST + '/api/price-parrot';
}

function getPriceSummaryJobs(
  queryParams: PriceSummaryJobQueryParams,
): Promise<PaginatedPriceParrotResponse<PriceSummaryJob>> {
  return request(`${basePath()}/price-summary/jobs?${qs.stringify(queryParams)}`, {
    method: 'GET',
    headers: json_headers,
  });
}

function postPriceSummaryJob(priceSummaryBody: PriceSummaryJobBody): Promise<{ result: { job_id: string } }> {
  return request(`${basePath()}/price-summary/jobs`, {
    headers: json_headers,
    method: 'POST',
    body: JSON.stringify(priceSummaryBody),
  });
}

function downloadPriceSummaryJob(jobId: string): Promise<Response> {
  return request(`${basePath()}/price-summary/jobs/${jobId}/download`, {
    returnResponse: true,
    method: 'GET',
  });
}

function getEventSourceURL(): string {
  return `${basePath()}/price-summary/jobs/status/stream`;
}

function cancelPriceSummaryJob(jobId: string): Promise<void> {
  return request(`${basePath()}/price-summary/jobs/${jobId}/cancel`, {
    method: 'POST',
    headers: json_headers,
  });
}

function getScheduledPriceSummaryJobs(
  queryParams: PaginatedPriceParrotQueryParams,
): Promise<PaginatedPriceParrotResponse<ScheduledPriceSummaryJob>> {
  return request(`${basePath()}/scheduled-price-summary/jobs?${qs.stringify(queryParams)}`, {
    method: 'GET',
    headers: json_headers,
  });
}

function postScheduledPriceSummaryJob(body: ScheduledPriceSummaryJobBody): Promise<{ job: ScheduledPriceSummaryJob }> {
  return request(`${basePath()}/scheduled-price-summary/jobs`, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(body),
  });
}

function patchScheduledPriceSummaryJob(
  jobId: number,
  body: Pick<ScheduledPriceSummaryJob, 'status' | 'scheduleInterval'>,
): Promise<{ job: ScheduledPriceSummaryJob }> {
  return request(`${basePath()}/scheduled-price-summary/jobs/${jobId}`, {
    method: 'PATCH',
    headers: json_headers,
    body: JSON.stringify(body),
  });
}

function getScheduledPriceSummaryJob(jobId: string): Promise<{ data: ScheduledPriceSummaryJob }> {
  return request(`${basePath()}/scheduled-price-summary/jobs/${jobId}`, { method: 'GET', headers: json_headers });
}

function postScheduledPriceSummaryJobVariation(
  jobId: number,
  body: ScheduledPriceSummaryJobVariationBody,
): Promise<{ data: ScheduledPriceSummaryJobVariation }> {
  return request(`${basePath()}/scheduled-price-summary/jobs/${jobId}/variations`, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(body),
  });
}

function deleteScheduledPriceSummaryJobVariation(jobId: number, variationId: number): Promise<void> {
  return request(`${basePath()}/scheduled-price-summary/jobs/${jobId}/variations/${variationId}`, {
    method: 'DELETE',
    headers: json_headers,
  });
}

function getScheduledPriceSummaryJobRuns(jobId: string): Promise<{ data: Array<ScheduledPriceSummaryJobRun> }> {
  return request(`${basePath()}/scheduled-price-summary/jobs/${jobId}/runs`, {
    method: 'GET',
    headers: json_headers,
  });
}

function downloadScheduledPriceSummaryJobRun(jobId: number, runId: number): Promise<Response> {
  return request(`${basePath()}/scheduled-price-summary/jobs/${jobId}/runs/${runId}/download`, {
    returnResponse: true,
    method: 'GET',
  });
}

function searchProperty(search: string): Promise<{ data: Array<Property> }> {
  return request(`${basePath()}/property/search?${qs.stringify({ search })}`, {
    method: 'GET',
    headers: json_headers,
  });
}

function vendorParityBasePath() {
  return window.configs.API_HOST + '/api/vendor/parity';
}

function mapRawProperty(raw: RawControlCentreProperty): ControlCentreProperty {
  return {
    propertyId: raw.property_id ?? raw.offer_bk,
    propertyName: raw.property_name ?? raw.offer_bk,
    offerBk: raw.offer_bk,
    vendorBk: raw.vendor_bk ?? null,
    offerCountryCode: raw.offer_country_code ?? null,
    offerCountryName: raw.offer_country_name ?? null,
    impressionsOverpriced: raw.impressions_overpriced_l30d ?? 0,
    pctImpressionsOverpriced: raw.pct_impressions_overpriced_l30d ?? 0,
    pctOverpriced: raw.pct_overpriced_l30d ?? 0,
    mpv7d: raw.mpv_l7d ?? 0,
    mpv30d: raw.mpv_l30d ?? 0,
    connectivityActionStatus: raw.sf_opportunity_status ?? null,
    lastActivityDate: raw.sf_last_activity ?? null,
    closedDate: raw.sf_closed_date ?? null,
    vnsDays7d: raw.vns_days ?? null,
    openOpp: raw.sf_opportunity_status === 'Open',
    bedbankLpcPct: raw.bedbank_lpc_ratio ?? null,
    yoyDeclinePct: raw.yoy_ttv_change ?? null,
    tier: raw.deal_tier != null ? parseInt(raw.deal_tier.match(/\d+$/)?.[0] ?? '', 10) || null : null,
  };
}

async function getControlCentreProperties(signal?: AbortSignal): Promise<Array<ControlCentreProperty>> {
  const response: RawControlCentreResponse = await request(`${vendorParityBasePath()}/control-centre`, {
    method: 'GET',
    headers: json_headers,
    signal,
  });

  return response.data.map(mapRawProperty);
}

function createConnectivityAction(body: CreateSfOpportunityBody): Promise<CreateSfOpportunityResponse> {
  return request(`${vendorParityBasePath()}/create-sf-opportunity`, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify({ property_id: body.propertyId }),
  });
}

function triggerExtranetNotification(body: TriggerNotificationBody): Promise<TriggerNotificationResponse> {
  return request(`${vendorParityBasePath()}/trigger-extranet-notification`, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify({ property_id: body.propertyId }),
  });
}

function bulkCreateSfOpportunities(body: BulkCreateSfOpportunitiesBody): Promise<BulkCreateSfOpportunitiesResponse> {
  return request(`${vendorParityBasePath()}/bulk-create-sf-opportunities`, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(body),
  });
}

export default {
  getPriceSummaryJobs,
  postPriceSummaryJob,
  downloadPriceSummaryJob,
  getEventSourceURL,
  cancelPriceSummaryJob,
  getScheduledPriceSummaryJobs,
  postScheduledPriceSummaryJob,
  patchScheduledPriceSummaryJob,
  getScheduledPriceSummaryJob,
  postScheduledPriceSummaryJobVariation,
  deleteScheduledPriceSummaryJobVariation,
  getScheduledPriceSummaryJobRuns,
  downloadScheduledPriceSummaryJobRun,
  searchProperty,
  getControlCentreProperties,
  createConnectivityAction,
  triggerExtranetNotification,
  bulkCreateSfOpportunities,
};
